/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Analytics } from '@analytics';
import DownArrowIcon from '@assets/images/DownArrow.svg';
import GroupIcon from '@assets/images/GroupIcon.svg';
import UpArrowIcon from '@assets/images/UpArrow.svg';
import TeamSettings from '@components/TapToTip/Groups/TeamSettings';
import { CardViewType } from '@constants';
import { AccountCircle as AccountCircleIcon } from '@material-ui/icons';
import { getSortedGuidesForTeam } from '@utils/helpers';
import { useInjectReducer } from '@utils/injectReducer';
import { useInjectSaga } from '@utils/injectSaga';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { getCurrentUser } from '../../../../store/user/actionCreators';
import GroupDetails from '../GroupDetails';
import * as actions from '../TeamSettings/actions';
import reducer from '../TeamSettings/reducer';
import saga from '../TeamSettings/saga';
import messages from './messages';

// Tailwind class strings shared within this component
const iconClasses = 'flex w-10 h-10 justify-center items-center rounded-full';
const avatarClasses = '-ml-2 bg-white shadow-md';

const GroupCard = ({
  user,
  group,
  cardOwnerType,
  cardOwnerElement,
  isUserInGroup,
  defaultOpenState = false,
  setDescription,
  setButtons,
  setDialogIcon,
  setIsConfirmationDialogShown,
  closeConfirmationDialog,
  fullSize = false,
  teamDetails,
  fetchTeamDetails,
  isLoading,
  getUser
}) => {
  useInjectSaga({ key: 'teamSettings', saga });
  useInjectReducer({ key: 'teamSettings', reducer });
  const intl = useIntl();
  const [cardView, setCardView] = useState(CardViewType.TeamDetails);
  const sortedUsers = useMemo(() => {
    if (!group?.users.length) return [];

    const currentUserId = user.userId;

    return getSortedGuidesForTeam(group.users, currentUserId);
  }, [group]);

  // Fetch team details only when group._id changes and teamDetails is missing
  useEffect(() => {
    if (group?._id && !teamDetails && !isLoading) {
      fetchTeamDetails(group._id);
    }
  }, [group?._id, teamDetails, isLoading, fetchTeamDetails]);

  // Only refetch user if user count mismatch is detected
  useEffect(() => {
    if (
      group &&
      teamDetails &&
      Array.isArray(teamDetails.users) &&
      Array.isArray(group.users) &&
      teamDetails.users.length !== group.users.length
    ) {
      getUser();
    }
  }, [group?.users?.length, teamDetails?.users?.length, getUser]);

  const [shouldShowGroupDetails, setShouldShowGroupDetails] = useState(defaultOpenState);

  const renderAvatars = () => {
    if (!sortedUsers?.length) return;

    return (
      <div className="flex flex-row">
        {sortedUsers.slice(0, 5).map(groupUser => {
          return (
            <div key={groupUser._id}>
              {groupUser.profile.avatarUrl ? (
                <img
                  className={`${iconClasses} ${avatarClasses}`}
                  src={groupUser.profile.avatarUrl}
                  alt="User avatar"
                />
              ) : (
                <AccountCircleIcon
                  className={`${iconClasses} ${avatarClasses} text-slate-300`}
                />
              )}
            </div>
          );
        })}
        {sortedUsers.length > 5 && (
          <div
            className={`${iconClasses} -ml-2 shadow-md bg-indigo-100 text-indigo-500 text-lg font-semibold`}>{`+${sortedUsers.length -
            5}`}</div>
        )}
      </div>
    );
  };

  if (cardView === CardViewType.TeamSettings) {
    return (
      <TeamSettings
        currentUser={user}
        usersInfo={group?.users}
        groupId={group?._id}
        setCardView={setCardView}
      />
    );
  }

  return (
    <>
      <div
        className={`px-5 py-4 shadow-sm border border-solid border-slate-200 rounded-3xl bg-slate-50 flex flex-col justify-start flex-[1_0_0%] m-auto w-full cursor-pointer ${
          fullSize ? 'sm:max-w-full' : 'sm:max-w-[400px]'
        }`}
        onClick={() => {
          Analytics.track(`${shouldShowGroupDetails ? 'close' : 'open'} team details`);
          setShouldShowGroupDetails(!shouldShowGroupDetails);
        }}>
        <div className="flex flex-row mb-5 -ml-2">
          <div className={iconClasses}>
            <img src={GroupIcon} alt="team icon" />
          </div>
          <div className="flex flex-col pl-2">
            <div className="text-base font-bold text-slate-800">{intl.formatMessage(messages.team)}</div>
            <div className="text-xs font-semibold text-slate-400">{`${intl.formatMessage(
              messages.members
            )} ${sortedUsers.length ?? 0} | ${intl.formatMessage(messages.resources)} ${group
              ?.resources?.length ?? 0}`}</div>
          </div>
        </div>
        <div className="flex justify-between">
          <div>{renderAvatars()}</div>
          {(sortedUsers.length || group?.resources?.length) && (
            <div className="flex items-end cursor-pointer">
              <img
                alt="Toggle team icon"
                src={shouldShowGroupDetails ? UpArrowIcon : DownArrowIcon}
              />
            </div>
          )}
        </div>
      </div>

      {(sortedUsers.length || group?.resources?.length) && shouldShowGroupDetails && (
        <div className="mt-5">
          <GroupDetails
            group={group}
            user={user}
            cardOwnerElement={cardOwnerElement}
            cardOwnerType={cardOwnerType}
            isUserInGroup={isUserInGroup}
            setDialogIcon={setDialogIcon}
            setDescription={setDescription}
            setButtons={setButtons}
            setIsConfirmationDialogShown={setIsConfirmationDialogShown}
            closeConfirmationDialog={closeConfirmationDialog}
            fullSize={fullSize}
            setCardView={setCardView}
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  isLoading: state.teamSettings?.isLoading,
  teamDetails: state.teamSettings?.teamDetails
});

const mapDispatchToProps = dispatch => ({
  fetchTeamDetails: groupId => dispatch(actions.fetchTeamDetails(groupId)),
  getUser: () => dispatch(getCurrentUser())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GroupCard);
